import { useState, useEffect, useCallback, useRef } from 'react';
import { format, addMinutes, parseISO, differenceInMinutes, isToday } from 'date-fns';
import { useAdmin } from '@/contexts/AdminContext';
import { toast } from 'sonner';

const DEFAULT_DURATION = 30;
const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Fetches calendar events, transforms them to RBC format, and provides
 * optimistic move/resize with undo support.
 */
export default function useCalendarEvents({ startDate, endDate, siteFilter = [] }) {
  const { apiFetch } = useAdmin();
  const [events, setEvents] = useState([]);
  const [rawEvents, setRawEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const snapshotRef = useRef(null);
  const undoTimerRef = useRef(null);
  const sseRef = useRef(null);

  // ── Fetch events ──
  const fetchEvents = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      setLoading(true);
      const start = format(startDate, 'yyyy-MM-dd');
      const end = format(endDate, 'yyyy-MM-dd');
      const res = await apiFetch(`/calendar/events?startDate=${start}&endDate=${end}`);
      const data = await res.json();
      setRawEvents(data.events || []);
      setError(null);
    } catch (err) {
      console.error('[useCalendarEvents] fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, apiFetch]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // ── Transform raw events to RBC format ──
  useEffect(() => {
    const transformed = rawEvents
      .filter(evt => {
        if (siteFilter.length === 0) return true;
        return siteFilter.includes(evt.siteKey);
      })
      .map(evt => {
        if (evt.type === 'cohort') {
          return {
            id: evt.id,
            title: evt.title,
            start: parseISO(evt.startDate),
            end: parseISO(evt.endDate),
            allDay: true,
            resource: evt.providerId,
            data: evt,
          };
        }

        // Booking event
        const date = evt.date || format(new Date(), 'yyyy-MM-dd');
        const time = evt.time || '09:00';
        const duration = evt.duration || DEFAULT_DURATION;
        const start = parseISO(`${date}T${time}`);
        const end = addMinutes(start, duration);

        // Parse patient name from title "Service - FirstName LastName"
        const parts = (evt.title || '').split(' - ');

        return {
          id: evt.id,
          title: evt.title,
          start,
          end,
          allDay: false,
          resource: evt.providerId,
          data: {
            ...evt,
            serviceName: parts[0] || '',
            patientName: parts.slice(1).join(' - ') || '',
            duration,
          },
        };
      });

    setEvents(transformed);
  }, [rawEvents, siteFilter]);

  // ── Today's events helpers ──
  const todayEvents = events.filter(e => !e.allDay && e.start && isToday(e.start));
  const todayCount = todayEvents.length;
  const nextAppointment = todayEvents
    .filter(e => e.start > new Date())
    .sort((a, b) => a.start - b.start)[0] || null;

  // ── Optimistic move (drag) ──
  const optimisticMove = useCallback(async (eventId, newStart, newEnd) => {
    const event = events.find(e => e.id === eventId);
    if (!event || event.data?.type !== 'booking') return;

    const bookingId = event.data.metadata?.bookingId;
    if (!bookingId) return;

    // Snapshot for undo
    snapshotRef.current = [...events];

    // Optimistic update
    setEvents(prev =>
      prev.map(e =>
        e.id === eventId ? { ...e, start: newStart, end: newEnd } : e
      )
    );

    try {
      const res = await apiFetch(`/bookings/${bookingId}/status`, {
        method: 'PUT',
        body: JSON.stringify({
          confirmedDate: format(newStart, 'yyyy-MM-dd'),
          confirmedTime: format(newStart, 'HH:mm'),
        }),
      });

      if (!res.ok) throw new Error('Failed to reschedule');

      // Success toast with undo
      clearTimeout(undoTimerRef.current);
      toast.success('Appointment rescheduled', {
        description: `Moved to ${format(newStart, 'EEE d MMM')} at ${format(newStart, 'HH:mm')}`,
        action: {
          label: 'Undo',
          onClick: () => {
            if (snapshotRef.current) {
              setEvents(snapshotRef.current);
              // Revert on server
              apiFetch(`/bookings/${bookingId}/status`, {
                method: 'PUT',
                body: JSON.stringify({
                  confirmedDate: format(event.start, 'yyyy-MM-dd'),
                  confirmedTime: format(event.start, 'HH:mm'),
                }),
              }).catch(() => toast.error('Failed to undo — please refresh'));
            }
          },
        },
        duration: 5000,
      });
    } catch (err) {
      // Revert on error
      if (snapshotRef.current) {
        setEvents(snapshotRef.current);
      }
      toast.error('Failed to reschedule', { description: err.message });
    }
  }, [events, apiFetch]);

  // ── Optimistic resize (duration change) ──
  const optimisticResize = useCallback(async (eventId, newStart, newEnd) => {
    const event = events.find(e => e.id === eventId);
    if (!event || event.data?.type !== 'booking') return;

    const bookingId = event.data.metadata?.bookingId;
    if (!bookingId) return;

    const newDuration = differenceInMinutes(newEnd, newStart);
    if (newDuration < 15) {
      toast.error('Minimum duration is 15 minutes');
      return;
    }
    if (newDuration > 480) {
      toast.error('Maximum duration is 8 hours');
      return;
    }

    // Snapshot for undo
    snapshotRef.current = [...events];
    const oldDuration = event.data.duration || DEFAULT_DURATION;

    // Optimistic update
    setEvents(prev =>
      prev.map(e =>
        e.id === eventId ? { ...e, start: newStart, end: newEnd, data: { ...e.data, duration: newDuration } } : e
      )
    );

    try {
      const res = await apiFetch(`/bookings/${bookingId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ duration: newDuration }),
      });

      if (!res.ok) throw new Error('Failed to update duration');

      toast.success('Duration updated', {
        description: `Changed from ${oldDuration}min to ${newDuration}min`,
        action: {
          label: 'Undo',
          onClick: () => {
            if (snapshotRef.current) {
              setEvents(snapshotRef.current);
              apiFetch(`/bookings/${bookingId}/status`, {
                method: 'PUT',
                body: JSON.stringify({ duration: oldDuration }),
              }).catch(() => toast.error('Failed to undo — please refresh'));
            }
          },
        },
        duration: 5000,
      });
    } catch (err) {
      if (snapshotRef.current) {
        setEvents(snapshotRef.current);
      }
      toast.error('Failed to update duration', { description: err.message });
    }
  }, [events, apiFetch]);

  // ── SSE for live updates ──
  useEffect(() => {
    let reconnectTimer;
    let retryCount = 0;

    function connect() {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const url = `${API_BASE}/api/v1/notifications/sse/events`;
        const es = new EventSource(`${url}?token=${token}`);
        sseRef.current = es;

        es.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            const refreshEvents = [
              'booking.created', 'booking.confirmed', 'booking.cancelled',
              'booking.rescheduled',
            ];
            if (refreshEvents.includes(data.type)) {
              // Debounced refetch
              clearTimeout(reconnectTimer);
              reconnectTimer = setTimeout(() => fetchEvents(), 1000);
              toast.info('Calendar updated', { duration: 2000 });
            }
          } catch { /* ignore parse errors */ }
        };

        es.onopen = () => { retryCount = 0; };

        es.onerror = () => {
          es.close();
          sseRef.current = null;
          // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
          const delay = Math.min(1000 * Math.pow(2, retryCount), 30000);
          retryCount++;
          reconnectTimer = setTimeout(connect, delay);
        };
      } catch { /* SSE not supported */ }
    }

    connect();

    return () => {
      clearTimeout(reconnectTimer);
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [fetchEvents]);

  return {
    events,
    loading,
    error,
    refetch: fetchEvents,
    optimisticMove,
    optimisticResize,
    todayCount,
    nextAppointment,
  };
}
